import { ChangeEvent, FC, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  InterviewGuideBlueprintQuery,
  useInterviewGuideBlueprintQuery,
  useInterviewGuideBlueprintQuestionAddSoftSkillMutation,
  useInterviewGuideBlueprintQuestionRemoveSoftSkillMutation,
  useInterviewGuideBlueprintQuestionUpdateSoftSkillMutation,
} from 'generated/graphql';
import QuestionCard from './components/QuestionCard';
import {
  emptyIndicator,
  IndicatorValues,
} from './components/EditIndicatorsDialog';

type QueriedIndicator =
  InterviewGuideBlueprintQuery['InterviewGuideBlueprint']['questions'][number]['softSkillJunctions'][number]['indicators'][number];

const toIndicatorValues = (indicator: QueriedIndicator): IndicatorValues => {
  if (indicator.__typename === 'SoftSkillIndicatorV2') {
    return {
      indicator: indicator.indicator,
      description: indicator.description,
      reason: indicator.reason,
      probe: indicator.probe,
      trigger: indicator.trigger,
      scores: indicator.scores.map(s => ({
        score: s.score,
        label: s.label,
        gateway_criteria: [...s.gateway_criteria],
        differentiation: s.differentiation,
      })),
      negative_evidence: {
        red_flags: indicator.negative_evidence.red_flags.map(p => ({
          pattern: p.pattern,
          example: p.example,
        })),
        score_reducing_patterns:
          indicator.negative_evidence.score_reducing_patterns.map(p => ({
            pattern: p.pattern,
            example: p.example,
          })),
      },
    };
  }
  return {
    ...emptyIndicator(),
    indicator: indicator.indicator,
    description: indicator.description,
    reason: indicator.reason,
    probe: indicator.probe,
    trigger: indicator.trigger,
  };
};

const isIndicatorValues = (value: unknown): value is IndicatorValues => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.indicator === 'string' &&
    typeof v.description === 'string' &&
    typeof v.reason === 'string' &&
    typeof v.probe === 'string' &&
    typeof v.trigger === 'string' &&
    Array.isArray(v.scores) &&
    typeof v.negative_evidence === 'object' &&
    v.negative_evidence !== null &&
    Array.isArray(
      (v.negative_evidence as Record<string, unknown>).red_flags,
    ) &&
    Array.isArray(
      (v.negative_evidence as Record<string, unknown>).score_reducing_patterns,
    )
  );
};

const InterviewGuideBlueprint: FC = () => {
  const { subId } = useParams() as { subId: string };

  const { data, loading, error, refetch } = useInterviewGuideBlueprintQuery({
    variables: { subId: subId! },
    skip: !subId,
    fetchPolicy: 'network-only',
  });

  const [addSoftSkill, { loading: addLoading }] =
    useInterviewGuideBlueprintQuestionAddSoftSkillMutation({
      onCompleted: () => refetch(),
    });

  const [removeSoftSkill, { loading: removeLoading }] =
    useInterviewGuideBlueprintQuestionRemoveSoftSkillMutation({
      onCompleted: () => refetch(),
    });

  const [updateSoftSkill, { loading: updateLoading }] =
    useInterviewGuideBlueprintQuestionUpdateSoftSkillMutation({
      onCompleted: () => refetch(),
    });

  const mutationLoading = addLoading || removeLoading || updateLoading;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const handleAddSoftSkill = useCallback(
    (
      questionSubId: string,
      softSkillId: number,
      indicators: IndicatorValues[],
    ) => {
      addSoftSkill({
        variables: {
          questionSubId,
          softSkillId,
          indicators: indicators.length > 0 ? indicators : undefined,
        },
      });
    },
    [addSoftSkill],
  );

  const handleRemoveSoftSkill = useCallback(
    (questionSubId: string, softSkillId: number) => {
      removeSoftSkill({
        variables: { questionSubId, softSkillId },
      });
    },
    [removeSoftSkill],
  );

  const handleUpdateIndicators = useCallback(
    (
      questionSubId: string,
      softSkillId: number,
      indicators: IndicatorValues[],
    ) => {
      updateSoftSkill({
        variables: { questionSubId, softSkillId, indicators },
      });
    },
    [updateSoftSkill],
  );

  const handleExport = useCallback(() => {
    const blueprint = data?.InterviewGuideBlueprint;
    if (!blueprint) return;

    const exported: Record<string, IndicatorValues[]> = {};
    for (const question of blueprint.questions) {
      for (const junction of question.softSkillJunctions) {
        const skillName = junction.softSkill.name;
        if (!(skillName in exported)) {
          exported[skillName] = junction.indicators.map(toIndicatorValues);
        }
      }
    }

    const blob = new Blob([JSON.stringify(exported, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blueprint-indicators-${subId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, subId]);

  const handleImport = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !data?.InterviewGuideBlueprint) return;

      try {
        setImporting(true);
        const text = await file.text();
        const parsed = JSON.parse(text) as unknown;

        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          alert('Invalid format: expected an object with skill names as keys.');
          return;
        }

        const imported = parsed as Record<string, unknown>;
        for (const [skillName, indicators] of Object.entries(imported)) {
          if (!Array.isArray(indicators) || !indicators.every(isIndicatorValues)) {
            alert(
              `Invalid V2 indicator shape for skill "${skillName}". ` +
                'Expected fields: indicator, description, reason, probe, trigger, scores[], negative_evidence{red_flags[], score_reducing_patterns[]}.',
            );
            return;
          }
        }
        const validated = imported as Record<string, IndicatorValues[]>;

        const mutations: Array<() => Promise<unknown>> = [];

        for (const question of data.InterviewGuideBlueprint.questions) {
          for (const junction of question.softSkillJunctions) {
            const skillName = junction.softSkill.name;
            if (skillName in validated) {
              mutations.push(() =>
                updateSoftSkill({
                  variables: {
                    questionSubId: question.subId,
                    softSkillId: junction.softSkill.id,
                    indicators: validated[skillName],
                  },
                }),
              );
            }
          }
        }

        for (const mutation of mutations) {
          await mutation();
        }

        await refetch();
      } catch (err) {
        alert(`Import failed: ${err instanceof Error ? err.message : err}`);
      } finally {
        setImporting(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [data, updateSoftSkill, refetch],
  );

  if (!subId) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">
          No blueprint subId provided in the URL.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }

  const blueprint = data?.InterviewGuideBlueprint;

  if (!blueprint) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Blueprint not found.</Typography>
      </Box>
    );
  }

  const questions = [...blueprint.questions]
    .sort((a, b) => a.position - b.position)
    .map(q => ({
      ...q,
      softSkillJunctions: q.softSkillJunctions.map(j => ({
        ...j,
        indicators: j.indicators.map(toIndicatorValues),
      })),
    }));

  return (
    <Box sx={{ p: 4, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Interview Guide Blueprint
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Chip label={`Status: ${blueprint.status}`} size="small" />
          <Chip label={`Version: ${blueprint.version}`} size="small" />
          <Chip
            label={`Generation: ${blueprint.generationStatus}`}
            size="small"
          />
          <Chip
            label={`subId: ${blueprint.subId}`}
            size="small"
            variant="outlined"
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
          >
            Export Indicators
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              importing ? <CircularProgress size={16} /> : <FileUploadIcon />
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Import Indicators'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
        </Box>
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Questions ({questions.length})
      </Typography>

      {questions.map(question => (
        <QuestionCard
          key={question.id}
          question={question}
          onAddSoftSkill={handleAddSoftSkill}
          onRemoveSoftSkill={handleRemoveSoftSkill}
          onUpdateIndicators={handleUpdateIndicators}
          mutationLoading={mutationLoading}
        />
      ))}
    </Box>
  );
};

export default InterviewGuideBlueprint;
